/**
 * Playing one metronome click in whichever sound is picked. Shared by the metronome engine and the
 * chord preview, which schedule on different audio contexts.
 */

import { CLICK_SOUNDS, getClickBuffer, getClickSound, getLayers, playLayers, prepareClickSound, variantLevel } from './clickSounds';

export const DEFAULT_SOUND = 'sideStick';

export const PICKER_SOUNDS = CLICK_SOUNDS.map(({ id, label }) => ({ value: id, label }));

// A saved sound that no longer exists, like one from before clickSounds.js, plays the default
export const resolveSoundType = (id) => (getClickSound(id) ? id : DEFAULT_SOUND);

// A click's peak at full volume. At 2, 50% lands about where the old sounds peaked.
const CLICK_GAIN = 2;

// Where the soft clip starts to bend, and how far past full scale its curve reaches
const SOFT_CLIP_KNEE = 0.6;
const SOFT_CLIP_RANGE = 3;

let softClipCurve = null;

const getSoftClipCurve = () => {
  if (!softClipCurve) {
    softClipCurve = new Float32Array(2048);

    for (let i = 0; i < softClipCurve.length; i++) {
      const x = ((i / (softClipCurve.length - 1)) * 2 - 1) * SOFT_CLIP_RANGE;
      const over = Math.abs(x) - SOFT_CLIP_KNEE;
      const y = over <= 0 ? Math.abs(x) : SOFT_CLIP_KNEE + (1 - SOFT_CLIP_KNEE) * Math.tanh(over / (1 - SOFT_CLIP_KNEE));

      softClipCurve[i] = Math.sign(x) * y;
    }
  }

  return softClipCurve;
};

const outputs = new WeakMap();

// A click turned up past full scale is rounded off rather than squared off
const getOutput = (ctx) => {
  if (!outputs.has(ctx)) {
    const input = ctx.createGain();
    const shaper = ctx.createWaveShaper();

    input.gain.value = 1 / SOFT_CLIP_RANGE;
    shaper.curve = getSoftClipCurve();
    shaper.oversample = '2x';
    input.connect(shaper);
    shaper.connect(ctx.destination);
    outputs.set(ctx, input);
  }

  return outputs.get(ctx);
};

/**
 * Schedule one click at an exact time on `ctx`.
 *
 * @param {string} variant - 'downbeat', 'regular' or 'subdivision'
 * @param {object} options.soundType - A CLICK_SOUNDS id; anything else plays the default
 * @param {number} options.volume - 0-1
 */
export const scheduleClickSound = (ctx, time, variant, { soundType, volume }) => {
  const sound = getClickSound(resolveSoundType(soundType));
  const gainNode = ctx.createGain();
  const buffer = getClickBuffer(ctx, sound.id, variant);

  gainNode.gain.value = volume * CLICK_GAIN;
  gainNode.connect(getOutput(ctx));

  if (!buffer) {
    // Not rendered yet, usually the very first beat: play the recipe live and render it for the next
    prepareClickSound(sound.id);
    gainNode.gain.value *= variantLevel(sound, variant);
    playLayers(ctx, gainNode, time, getLayers(sound, variant));

    return;
  }

  const source = ctx.createBufferSource();

  source.buffer = buffer;
  source.connect(gainNode);
  source.start(time);

  source.onended = () => {
    source.disconnect();
    gainNode.disconnect();
  };
};

const AUDITION_GAP = 0.45;

let auditionContext = null;

/** A downbeat then a regular beat, so a sound can be heard as it's picked. Call from a tap. */
export const auditionSound = (soundType, volume) => {
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;

  if (!AudioContextClass) return;

  if (!auditionContext) auditionContext = new AudioContextClass();
  if (auditionContext.state === 'suspended') auditionContext.resume();

  const start = auditionContext.currentTime + 0.05;

  scheduleClickSound(auditionContext, start, 'downbeat', { soundType, volume });
  scheduleClickSound(auditionContext, start + AUDITION_GAP, 'regular', { soundType, volume });
};
